import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function AuthPage({ onBack, initialMode = "login" }) {
  const { login, register, loading } = useAuth();
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(initialMode === "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && !name.trim()) { setError(t("auth_error_name")); return; }
    if (!email.trim()) { setError(t("auth_error_email")); return; }
    if (!password) { setError(t("auth_error_password")); return; }
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4 relative font-sans">
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-6 left-6 flex items-center gap-2 text-text-muted hover:text-primary transition-colors text-sm"
        >
          <ArrowLeft size={16} />
          {t("auth_back")}
        </button>
      )}
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Beauly" className="h-20 object-contain mx-auto mb-4" />
          <h1 className="text-4xl font-display text-primary mb-2">
            Beauly
          </h1>
          <p className="text-text-muted">{t("auth_subtitle")}</p>
        </div>

        <div className="rounded-2xl border border-surface-border bg-surface p-8">
          <h2 className="text-xl font-semibold mb-6 text-primary">
            {isLogin ? t("auth_login") : t("auth_register")}
          </h2>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {!isLogin && (
              <input
                type="text"
                placeholder={t("auth_name")}
                value={name}
                onChange={(e) => { setName(e.target.value); setError(""); }}
                className={`w-full px-4 py-3 rounded-lg bg-white border ${error && !name.trim() && !isLogin ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
              />
            )}
            <input
              type="email"
              placeholder={t("auth_email")}
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              className={`w-full px-4 py-3 rounded-lg bg-white border ${error && !email.trim() ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
            />
            <input
              type="password"
              placeholder={t("auth_password")}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              className={`w-full px-4 py-3 rounded-lg bg-white border ${error && !password ? 'border-red-400' : 'border-surface-borderDark'} text-primary placeholder-text-placeholder focus:border-accent focus:ring-2 focus:ring-primary-50 outline-none transition`}
            />

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-accent text-white rounded-lg font-medium hover:bg-accent-hover transition disabled:opacity-50"
            >
              {loading ? "..." : isLogin ? t("auth_login") : t("auth_register")}
            </button>
          </form>

          <div className="mt-4 text-center">
            <button
              onClick={() => { setIsLogin(!isLogin); setError(""); }}
              className="text-accent-dark text-sm hover:underline"
            >
              {isLogin ? t("auth_no_account") : t("auth_has_account")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
